import {isArticleType} from 'Application/utils/typeGuards';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';

/**
 * Custom hook that processes news article metadata to provide formatted date information.
 * This hook extracts the creation date from article metadata and formats it according to German locale standards.
 * It safely handles cases where metadata might be undefined or not contain valid article data.
 *
 * @param metaData Optional page metadata containing structured data information.
 * @returns Object containing the formatted date string and original time value.
 *
 * @example
 * ```tsx
 * const { formattedTime, time } = useNewsIntroBlock(pageMetaData);
 *
 * return (
 *   <div>
 *     {formattedTime && <time dateTime={time}>{formattedTime}</time>}
 *   </div>
 * );
 * ```
 */
export const useNewsIntroBlock = (metaData?: PageData['pageMetaData']) => {
    let time: string | undefined;
    let formattedTime;

    if (metaData?.structuredData.listData && isArticleType(metaData.structuredData.listData)) {
        time = metaData.structuredData.listData.createdAt!;
        formattedTime = Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(new Date(time));
    }

    return {
        formattedTime,
        time
    };
};